/**
 * Layouts (dostępne układy) dla templatu CMS `default`.
 *
 * Server-safe — plik BEZ `"use client"`. Konsumowane przez Server Components
 * (renderery layoutu w `app/[locale]/page.tsx` + `app/[locale]/[...slug]/page.tsx`)
 * i przez admin client UI (`LayoutAndWindowsPanel`).
 *
 * Po cutover 2026-05-27 (IV) — definicje layoutów żyją per template
 * (analog do legacy Imperial: `template/{symbol}/layout/*.imp` — pliki view
 * per template, kontrolery wspólne). Wcześniej `imp_cms_layout` + `imp_cms_layout_areas`
 * + `imp_cms_domain_template_layout` w DB.
 *
 * **Każdy template ma OWN listę:**
 *   - `homepageLayouts` — layouty dla strony głównej (`PageLayoutMeta[]`)
 *   - `catalogLayouts`  — layouty dla podstron-katalogów (`CatalogLayoutMeta[]`)
 *
 * **Komponenty React layoutów wspólne** w `components/layouts/pages/*` i
 * `components/layouts/catalogs/*` — używane przez wszystkie templates.
 * Per-template różni się tylko zestaw aktywnych + opcjonalnie nazwy areas /
 * sortowanie.
 */

import type {
  PageLayoutMeta,
  CatalogLayoutMeta,
} from "@/lib/layouts/types";

/* ─── Homepage layouts (strona główna) ────────────────────────────────── */

export const homepageLayouts: PageLayoutMeta[] = [
  {
    symbol: "LSingleColumn",
    name: "Pojedyncza kolumna",
    areas: [
      { symbol: "heroLarge", name: "Hero" },
      { symbol: "main", name: "Główny obszar" },
    ],
  },
  {
    symbol: "LTwoColsLeft",
    name: "Dwie kolumny (lewa + główna)",
    areas: [
      { symbol: "leftSidebar", name: "Lewa kolumna" },
      { symbol: "main", name: "Główny obszar" },
    ],
  },
  {
    symbol: "LTwoColsRight",
    name: "Dwie kolumny (główna + prawa)",
    areas: [
      { symbol: "main", name: "Główny obszar" },
      { symbol: "rightSidebar", name: "Prawa kolumna" },
    ],
  },
  {
    symbol: "LThreeCols",
    name: "Trzy kolumny",
    areas: [
      { symbol: "leftSidebar", name: "Lewa kolumna" },
      { symbol: "main", name: "Główny obszar" },
      { symbol: "rightSidebar", name: "Prawa kolumna" },
    ],
  },
];

/* ─── Catalog layouts (podstrony z catalogContent area) ───────────────── */

/**
 * Catalog layouts — każdy ma 2 body slots na main column:
 *   - catalogContent (BLOCKED, content z catalog management — NIE drop target)
 *   - `belowCatalog` (window area)
 * + opcjonalne sidebars (left/right).
 */
export const catalogLayouts: CatalogLayoutMeta[] = [
  {
    symbol: "LCatalogSingleColumn",
    name: "Pojedyncza kolumna (zwężona)",
    areas: [
      { symbol: "belowCatalog", name: "Pod treścią katalogu" },
    ],
  },
  {
    symbol: "LCatalogSingleColumnFull",
    name: "Pojedyncza kolumna (pełna szerokość)",
    areas: [
      { symbol: "belowCatalog", name: "Pod treścią katalogu" },
    ],
  },
  {
    symbol: "LCatalogTwoColsLeft",
    name: "Dwie kolumny (lewa + treść)",
    areas: [
      { symbol: "leftSidebar", name: "Lewa kolumna" },
      { symbol: "belowCatalog", name: "Pod treścią katalogu" },
    ],
  },
  {
    symbol: "LCatalogTwoColsRight",
    name: "Dwie kolumny (treść + prawa)",
    areas: [
      { symbol: "belowCatalog", name: "Pod treścią katalogu" },
      { symbol: "rightSidebar", name: "Prawa kolumna" },
    ],
  },
  {
    symbol: "LCatalogThreeCols",
    name: "Trzy kolumny",
    areas: [
      { symbol: "leftSidebar", name: "Lewa kolumna" },
      { symbol: "belowCatalog", name: "Pod treścią katalogu" },
      { symbol: "rightSidebar", name: "Prawa kolumna" },
    ],
  },
  {
    /* Landing page - pelny full bleed, BEZ chrome (HDefault header + FDefault
       footer + top message). 3 sekcje pionowo: aboveCatalog (okna) ->
       catalogContent (tresc katalogu, BLOCKED area) -> belowCatalog (okna).
       aboveCatalog i belowCatalog to drop targets dla okien. */
    symbol: "LCatalogLanding",
    name: "Landing page (bez naglowka i stopki)",
    areas: [
      { symbol: "aboveCatalog", name: "Nad treścią katalogu" },
      { symbol: "belowCatalog", name: "Pod treścią katalogu" },
    ],
    hideChrome: true,
  },
];
